import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import { observable, action, makeObservable } from 'mobx'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { COURSE_COMMENTS_CLASS_NAMES } from '@elo-kit/constants/customCss.constants'
import { getBackgroundImageCss } from '@elo-kit/utils/themes.utils'

import { formatDateTime } from '@elo-kit/utils/dateTime.utils'
import { createId } from '@elo-kit/utils/general.utils'

import { ROOT_URL } from 'constants/general.constants'
import { PROFILES } from 'constants/profile.constants'

import { EloButton } from '@elo-kit/components/elo-button/EloButton'
import { TextEditorField } from '@elo-kit/components/form/text-editor-field/TextEditorField'
import CommentsForm from './CommentsForm'

const propTypes = {
  comment: PropTypes.shape({}).isRequired,
  addComment: PropTypes.func,
  sellerId: PropTypes.number,
  currentProfileId: PropTypes.number,
}

@observer
class Comment extends Component {
  @observable editMode = false
  @observable scrollCondition = false
  @observable commentContent = ''

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  setStateValue = (state) => (value) => (this[state] = value)

  @action setCommentContent = this.setStateValue('commentContent')

  @action toggleEditMode = () => (this.editMode = !this.editMode)

  componentDidMount() {
    const { comment, commentId } = this.props

    this.scrollCondition = +commentId === comment.id

    if (this.scrollCondition) {
      setTimeout(() => {
        const query = document.getElementById(commentId)
        if (query) {
          query.scrollIntoView()
          window.scrollBy(0, -60)
        }
      }, 500)
    }
  }

  render() {
    const {
      addComment,
      avatarUrl,
      comment,
      commentId,
      currentProfileId,
      currentProfileType,
      customView,
      editComment,
      deleteComment,
      sellerId,
      fontFamilyList,
      showCommentsArea,
      toggleCommentsArea,
      replyId,
      setReplyId,
    } = this.props
    const { body, bodyHtml, children, createdAt, deleted, id, sender } = comment
    const { avatar, id: senderId, name, publicName } = sender

    const isPayerCabinet = currentProfileType === PROFILES.payer

    const isOwnComment = currentProfileId === senderId

    const hasReplies = children.length

    const commentsWrapClasses = classNames('comments-wrap', {
      'deleted-comment': deleted && !hasReplies,
    })

    const commentContainerClasses = classNames('comment-container', {
      'highlight-comment': this.scrollCondition,
    })

    const deletedPlaceholder = hasReplies ? (
      <div className='deleted-comment-placeholder'>{I18n.t('react.shared.comments.commentWasRemoved')}</div>
    ) : null

    const handleEditComment = () => {
      editComment(id, this.commentContent)
      this.toggleEditMode()
    }

    const handleRedirectToProfile = () => {
      if (isPayerCabinet && isOwnComment) {
        window.open(`${ROOT_URL}/payer/profile/edit`, '_blank')
      }

      if (!isPayerCabinet) {
        if (isOwnComment) {
          window.open(`${ROOT_URL}/cabinet/profile/edit`, '_blank')
        } else {
          window.open(`${ROOT_URL}/cabinet/payers/${senderId}`, '_blank')
        }
      }
    }

    const senderClasses = classNames('sender', {
      'sender--pointer': isOwnComment || !isPayerCabinet,
    })

    return (
      <div className={commentsWrapClasses} id={id}>
        <div className={commentContainerClasses}>
          {deleted ? (
            deletedPlaceholder
          ) : (
            <Fragment>
              <div className={senderClasses}>
                <div
                  className='round-image-36'
                  style={{ backgroundImage: getBackgroundImageCss(avatar) }}
                  onClick={handleRedirectToProfile}
                />
              </div>

              <div className={classNames('comment-content', COURSE_COMMENTS_CLASS_NAMES.containerClassName)}>
                <div className='comment-body-top'>
                  <div className='comment-author'>
                    <span className='comment-name' onClick={handleRedirectToProfile}>
                      {publicName || name} {isOwnComment && `(${I18n.t('shared.common.you')})`}
                    </span>

                    <span className='comment-time'>{formatDateTime(createdAt)}</span>
                  </div>

                  <div className='own-comment-buttons'>
                    {isOwnComment && <i className='fas fa-pencil-alt' onClick={this.toggleEditMode} />}
                    {!isOwnComment && <i className='far fa-comment' onClick={() => setReplyId(id)} />}
                    {(isOwnComment || currentProfileType === 'seller') && (
                      <i className='far fa-trash-alt' onClick={() => deleteComment(id)} />
                    )}
                  </div>
                </div>

                <span className='comment-body fr-view'>
                  {this.editMode ? (
                    <div className='comment-body__container fields-container--without-margins'>
                      <TextEditorField
                        name='comment'
                        value={body}
                        onChange={this.setCommentContent}
                        fontFamilyList={fontFamilyList}
                        className={window.innerWidth < 991 ? 'text-editor-field--wrapper-height-100' : ''}
                      />
                      <div className='elo-btn-container elo-btn-container--right'>
                        <EloButton type='button' onClick={this.toggleEditMode} outlined>
                          {I18n.t('react.shared.button.cancel')}
                        </EloButton>

                        <EloButton type='button' onClick={handleEditComment}>
                          {I18n.t('react.shared.button.save')}
                        </EloButton>
                      </div>
                    </div>
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                  )}
                </span>
              </div>
            </Fragment>
          )}
        </div>

        {replyId === id && (
          <CommentsForm
            isReply
            handleReply={setReplyId}
            commentId={id}
            fontFamilyList={fontFamilyList}
            showCommentsArea={showCommentsArea}
            toggleCommentsArea={toggleCommentsArea}
            avatarUrl={avatarUrl || avatar}
            {...{
              addComment,
              customView,
            }}
          />
        )}

        {children.slice().length > 0 && (
          <div className='subcomments'>
            {children.map((comment, index) => (
              <Comment
                key={createId(id, index)}
                {...{
                  addComment,
                  comment,
                  commentId,
                  currentProfileId,
                  currentProfileType,
                  deleteComment,
                  editComment,
                  sellerId,
                  showCommentsArea,
                  toggleCommentsArea,
                  replyId,
                  setReplyId,
                  customView,
                }}
              />
            ))}
          </div>
        )}
      </div>
    )
  }
}

Comment.displayName = 'Comment'
Comment.propTypes = propTypes

export default Comment
